/**
 * Portfolio overview — the landing screen. SPEC-0007.
 *
 * Replaces the old "here's your latest run" summary: a project is now a
 * persistent thing (the `Project` record), so the landing screen is the
 * portfolio itself — every active project, ranked by what needs attention —
 * rather than one reconciliation event. The latest-run view this used to
 * show didn't disappear; `Runs` already lists every run in full, reachable
 * from the nav, so nothing here duplicates it.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { BAND_RANK } from '@picklii/recon';
import type { Project } from '@picklii/shared';
import { AppShell } from '../components/AppShell';
import { Icon } from '../components/Icon';
import { ProjectCard } from '../components/ProjectCard';
import { api } from '../lib/api';
import { money } from '../lib/format';

type Tab = 'active' | 'archived';
type BusyAction = 'archive' | 'dates';

/**
 * Most urgent first, then soonest-ending first — SPEC-0007 Recommendation
 * §3's "sortable so the most urgent and the soonest-ending surface first,"
 * implemented as a fixed default order rather than user-facing sort
 * controls, which the spec's acceptance criteria don't ask for.
 */
function sortProjects(projects: Project[]): Project[] {
	return [...projects].sort((a, b) => {
		const rankA = a.latestSeverityBand === null ? -1 : BAND_RANK[a.latestSeverityBand];
		const rankB = b.latestSeverityBand === null ? -1 : BAND_RANK[b.latestSeverityBand];
		if (rankA !== rankB) return rankB - rankA;

		if (a.endDate !== b.endDate) {
			if (a.endDate === null) return 1;
			if (b.endDate === null) return -1;
			return a.endDate.localeCompare(b.endDate);
		}
		return a.projectId.localeCompare(b.projectId);
	});
}

export function Dashboard() {
	const [projects, setProjects] = useState<Project[] | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [tab, setTab] = useState<Tab>('active');
	const [busy, setBusy] = useState<Record<string, BusyAction>>({});

	useEffect(() => {
		api.listProjects().then(setProjects, (cause: Error) => setError(cause.message));
	}, []);

	const active = useMemo(
		() => sortProjects((projects ?? []).filter((p) => !p.archived)),
		[projects],
	);
	const archived = useMemo(
		() => sortProjects((projects ?? []).filter((p) => p.archived)),
		[projects],
	);

	const shown = tab === 'active' ? active : archived;

	async function runUpdate(projectId: string, action: BusyAction, patch: Parameters<typeof api.updateProject>[1]) {
		setBusy((current) => ({ ...current, [projectId]: action }));
		setError(null);
		try {
			const updated = await api.updateProject(projectId, patch);
			setProjects((current) => (current ?? []).map((p) => (p.projectId === projectId ? updated : p)));
		} catch (cause) {
			setError(cause instanceof Error ? cause.message : 'Could not update that project.');
		} finally {
			setBusy((current) => {
				const next = { ...current };
				delete next[projectId];
				return next;
			});
		}
	}

	const totalExact = active.reduce((sum, p) => sum + p.latestExactExposureCents, 0);
	const totalEstimated = active.reduce((sum, p) => sum + p.latestEstimatedExposureCents, 0);
	const urgentCount = active.filter(
		(p) => p.latestSeverityBand === 'Critical' || p.latestSeverityBand === 'High',
	).length;

	return (
		<AppShell
			title="Dashboard"
			actions={
				<Link to="/runs/new" className="btn btn-sm btn-primary">
					<Icon name="upload" />
					New reconciliation
				</Link>
			}>
			{error !== null && (
				<div className="alert alert-error alert-soft col-span-12" role="alert">
					<span>{error}</span>
				</div>
			)}

			{projects === null && error === null && (
				<div className="col-span-12 flex justify-center py-16">
					<span className="loading loading-spinner" />
				</div>
			)}

			{projects !== null && projects.length === 0 && (
				<section className="card bg-base-200 border-base-300 col-span-12 border shadow-xs">
					<div className="card-body items-center text-center">
						<Icon name="upload" className="size-8 opacity-70" />
						<h2 className="card-title text-base">Nothing reconciled yet</h2>
						<p className="text-base-content/70 max-w-md text-sm">
							Upload a sold-hours export and a PSA budget export. Picklii matches them on project
							and role, diffs them, and ranks what it finds by urgency. Every project it finds
							shows up here.
						</p>
						<Link to="/runs/new" className="btn btn-primary btn-sm mt-2">
							Start the first one
						</Link>
					</div>
				</section>
			)}

			{projects !== null && projects.length > 0 && (
				<>
					<div className="tabs tabs-box col-span-12 w-fit">
						<button
							type="button"
							className={`tab ${tab === 'active' ? 'tab-active' : ''}`}
							onClick={() => setTab('active')}>
							Active
							<span className="badge badge-sm ms-2">{active.length}</span>
						</button>
						<button
							type="button"
							className={`tab ${tab === 'archived' ? 'tab-active' : ''}`}
							onClick={() => setTab('archived')}>
							Archived
							<span className="badge badge-sm ms-2">{archived.length}</span>
						</button>
					</div>

					{tab === 'active' && active.length > 0 && (
						<section className="stats stats-vertical bg-base-200 border-base-300 sm:stats-horizontal col-span-12 border shadow-xs">
							<div className="stat">
								<div className="stat-title">Active exposure — measured</div>
								<div className="stat-value text-xl font-semibold tabular-nums">
									{money(totalExact)}
								</div>
							</div>
							<div className="stat">
								<div className="stat-title">Active exposure — estimated</div>
								<div className="stat-value text-xl font-semibold tabular-nums">
									{money(totalEstimated)}
								</div>
								<div className="stat-desc">Reported separately, never summed with measured</div>
							</div>
							<div className="stat">
								<div className="stat-figure">
									<Icon name="warning" className="size-7 opacity-70" />
								</div>
								<div className="stat-title">Critical / High projects</div>
								<div className="stat-value text-xl font-semibold tabular-nums">{urgentCount}</div>
								<div className="stat-desc">Out of {active.length} active</div>
							</div>
						</section>
					)}

					{shown.length === 0 && (
						<div className="col-span-12 py-8 text-center">
							<p className="text-base-content/70 text-sm">
								{tab === 'active'
									? 'No active projects. Every reconciled project has been archived.'
									: 'No archived projects.'}
							</p>
						</div>
					)}

					<div className="col-span-12 grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
						{shown.map((project) => (
							<ProjectCard
								key={project.projectId}
								project={project}
								busyAction={busy[project.projectId]}
								onArchiveToggle={() =>
									void runUpdate(project.projectId, 'archive', { archived: !project.archived })
								}
								onSaveDates={(dates) => void runUpdate(project.projectId, 'dates', dates)}
							/>
						))}
					</div>
				</>
			)}
		</AppShell>
	);
}
